import type { AdminGuide } from "../adminGuides.types";
import { GUIDE_SOURCE_DOCS } from "./sourceDocs";

export const partnerPortalInvitesGuide: AdminGuide = {
  id: "partner-portal-invites",
  title: "Partner Portal: invite a client and verify they’re linked",
  category: "Demo & Partner",
  audience: ["Support", "CS"],
  summary:
    "Create a partner invite link or email invite, share it safely, and confirm the client becomes linked in the Partner Dashboard.",
  keywords: [
    "partner portal",
    "partner dashboard",
    "invite",
    "invite link",
    "inviteCode",
    "client onboarding",
    "linked organisation",
  ],
  location: "CustomerApp",
  risk: "medium",
  sourceDocs: [
    GUIDE_SOURCE_DOCS.PARTNER_DASHBOARD,
    GUIDE_SOURCE_DOCS.PARTNER_PORTAL_RUNBOOK,
    GUIDE_SOURCE_DOCS.ONBOARDING_APP,
  ],
  warnings: [
    "Invite links containing `inviteCode=...` are credential-like. Do not paste them into Slack tickets, PRs, or docs.",
    "Stop if you are unsure you are acting on the correct partner account (partnerId context).",
  ],
  safetyNotes: [
    "Partner Portal is partner-scoped, not organisation-scoped. The user must have partner membership for the selected partnerId.",
    "If you need to reproduce the flow as the partner user, use spoofing only with permission and follow the spoofing guide.",
  ],
  whatThisHelpsYouDo: [
    "Create a self-serve invite link or an email invite from the Partner Dashboard.",
    "Help a partner share the correct invite with a client.",
    "Verify the client organisation becomes linked to the partner after onboarding.",
  ],
  whenToUse: [
    "A partner wants to onboard a new client via an invite link.",
    "You need to confirm whether an invite was created and accepted.",
    "You are troubleshooting “client not appearing in the partner dashboard” issues.",
  ],
  whenNotToUse: [
    "The user is not a partner member or you cannot confirm partner membership.",
    "You are onboarding a normal (non-partner) customer (use the standard onboarding flow).",
  ],
  beforeYouStart: [
    "Partner user email (who is doing the inviting).",
    "Partner context: partnerId (if the user belongs to multiple partners).",
    "Client contact details (email) if using email invites.",
    "Environment (Preview vs Production).",
  ],
  steps: [
    "Have the partner user log in to the customer app.",
    "Open the **Partner Dashboard** (`/partner/dashboard`).",
    "If prompted, select the correct partner account (or ensure the URL includes `?partnerId=...`).",
    "Open the **Invites** tab.",
    "Create an invite link (self-serve) or create an email invite (if the product surface supports it).",
    "If you create an invite link, it produces a URL containing `inviteCode=...`.",
    "Share the invite only with the intended client using an approved channel. Do not paste it into Slack/tickets.",
    "Client uses the invite link to start onboarding and completes the onboarding steps.",
    "Return to the Partner Dashboard and verify the new client appears in the **Clients** tab and the invite is no longer pending.",
  ],
  whatSuccessLooksLike: [
    "The partner can create an invite (link or email invite) successfully.",
    "The client completes onboarding using the invite link.",
    "The client organisation appears in the Partner Dashboard Clients list.",
  ],
  troubleshooting: [
    {
      title: "Partner dashboard loads but shows the wrong partner account",
      body: [
        "Confirm the user belongs to multiple partners.",
        "Ensure the selected partner context is correct (URL `?partnerId=...`).",
        "If the UI cannot select the correct partnerId, escalate with the user’s email and the expected partnerId.",
      ],
    },
    {
      title: "Client says the invite link is invalid or expired",
      body: [
        "Create a new invite link and share it via an approved channel.",
        "Avoid copy/paste through systems that rewrite URLs.",
        "Escalate if invites consistently fail (include partnerId and approximate timestamps; do not include the inviteCode).",
      ],
    },
    {
      title: "Client completed onboarding but does not appear as linked",
      body: [
        "Confirm the client used the invite link (not the generic onboarding landing page).",
        "Ask for the approximate time onboarding was completed and the client org name.",
        "Escalate with partnerId + timestamps and request Engineering check invite acceptance and linking.",
      ],
    },
  ],
  escalateWhen: [
    "You cannot confirm which partner account (partnerId) is in scope.",
    "Invites fail consistently or linking does not occur after onboarding.",
    "The user reports cross-partner visibility or access problems (potential authz issue).",
  ],
  relatedGuides: [
    { label: "Onboarding support", guideId: "onboarding-support" },
    { label: "Submitting a bug report", guideId: "bug-reporting" },
    {
      label: "Spoofing (impersonation) safely",
      guideId: "spoofing-safely",
    },
  ],
  relatedAdminPaths: [{ label: "Users (for identity lookup)", path: "/users" }],
};
